/**
 * Redux slice (src/store/slices/coursesSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  CreateCourse,
  DeleteCourses,
  GetCourseByID,
  Getcourses,
  SearchCoursebyName,
  fetch5courses,
  fetchcourseDetails,
  updateCourse,
} from "../actions/coursesActions";
import { toast } from "react-toastify";
import { t } from "../../utils/i18nTranslate";

let intialState = {
  courseslist: [],
  courseDetails: [],
  AllCourseslist: [],
  CourseByID: [],
  loading: false,
  error: null,
  // Keep the public detail lifecycle independent from legacy course-list and
  // admin mutations that still share this slice.
  courseDetailsLoading: false,
  courseDetailsError: null,
  courseDetailsCourseId: null,
  courseDetailsRequestId: null,
};

/**
 * RTK slice definition: coursesSlice.
 */
const coursesSlice = createSlice({
  name: "courses",
  initialState: intialState,
  reducers: {},
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: fetch5courses.pending
      .addCase(fetch5courses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: fetch5courses.fulfilled
      .addCase(fetch5courses.fulfilled, (state, action) => {
        state.courseslist = action.payload;
        state.loading = false;
        state.error = null;
      })
      // extraReducers handler: fetch5courses.rejected
      .addCase(fetch5courses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // extraReducers handler: fetchcourseDetails.pending
      .addCase(fetchcourseDetails.pending, (state, action) => {
        state.courseDetailsLoading = true;
        state.courseDetailsError = null;
        state.courseDetails = null;
        state.courseDetailsCourseId = action.meta?.arg || null;
        state.courseDetailsRequestId = action.meta?.requestId || null;
      })
      // extraReducers handler: fetchcourseDetails.fulfilled
      .addCase(fetchcourseDetails.fulfilled, (state, action) => {
        if (action.meta?.requestId && state.courseDetailsRequestId !== action.meta.requestId) return;
        state.courseDetails = action.payload;
        state.courseDetailsLoading = false;
        state.courseDetailsError = null;
      })
      // extraReducers handler: fetchcourseDetails.rejected
      .addCase(fetchcourseDetails.rejected, (state, action) => {
        if (action.meta?.requestId && state.courseDetailsRequestId !== action.meta.requestId) return;
        state.courseDetailsLoading = false;
        state.courseDetailsError = action.meta?.aborted
          ? null
          : (action.payload || { status: 0, code: "COURSE_UNAVAILABLE" });
      })
      // extraReducers handler: Getcourses.pending
      .addCase(Getcourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: Getcourses.fulfilled
      .addCase(Getcourses.fulfilled, (state, action) => {
        state.courseslist = action.payload;
        state.AllCourseslist = action.payload;
        state.loading = false;
        state.error = null;
      })
      // extraReducers handler: Getcourses.rejected
      .addCase(Getcourses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // extraReducers handler: DeleteCourses.pending
      .addCase(DeleteCourses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: DeleteCourses.fulfilled
      .addCase(DeleteCourses.fulfilled, (state, action) => {
        const deletedCourseId = action.payload?.CourseDeleted?._id;
        if (!deletedCourseId) {
          state.loading = false;
          state.error = 'Course deletion response was invalid.';
          toast.error(t("store.courses.deleteFailed"));
          return;
        }

        // Handle both array and object with courses array structure
        if (Array.isArray(state.AllCourseslist)) {
          state.AllCourseslist = state.AllCourseslist.filter(
            (course) => String(course._id) !== String(deletedCourseId)
          );
        } else if (state.AllCourseslist && state.AllCourseslist.courses) {
          state.AllCourseslist.courses = state.AllCourseslist.courses.filter(
            (course) => String(course._id) !== String(deletedCourseId)
          );
        }
        state.loading = false;
        state.error = null;
        toast.success(t("store.courses.deletedSuccessfully"));
      })
      // extraReducers handler: DeleteCourses.rejected
      .addCase(DeleteCourses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || { status: 0, code: "COURSE_DELETE_UNAVAILABLE" };
        toast.error(t("store.courses.deleteFailed"));
      })
      // extraReducers handler: CreateCourse.pending
      .addCase(CreateCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: CreateCourse.fulfilled
      .addCase(CreateCourse.fulfilled, (state, action) => {
        // Handle both array and object with courses array structure
        if (Array.isArray(state.AllCourseslist)) {
          state.AllCourseslist.push(action.payload.newCourse);
        } else if (state.AllCourseslist && state.AllCourseslist.courses) {
          state.AllCourseslist.courses.push(action.payload.newCourse);
        } else {
          // If AllCourseslist is empty or null, initialize it properly
          state.AllCourseslist = {
            courses: [action.payload.newCourse]
          };
        }
        state.loading = false;
        state.error = null;
        toast.success(t("store.courses.createdSuccessfully"));
      })
      // extraReducers handler: CreateCourse.rejected
      .addCase(CreateCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.courses.createFailed"));
      })
      // extraReducers handler: updateCourse.pending
      .addCase(updateCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: updateCourse.fulfilled
      .addCase(updateCourse.fulfilled, (state, action) => {
        // Extract the course ID and updated data from the response
        const courseId = action.payload.courseId;
        const updatedData = action.payload.updatedData;
        
        // Handle both array and object with courses array structure
        let coursesArray = null;
        let isDirectArray = false;
        
        if (Array.isArray(state.AllCourseslist)) {
          coursesArray = state.AllCourseslist;
          isDirectArray = true;
        } else if (state.AllCourseslist && Array.isArray(state.AllCourseslist.courses)) {
          coursesArray = state.AllCourseslist.courses;
          isDirectArray = false;
        }
        
        if (coursesArray) {
          const updatedCourseIndex = coursesArray.findIndex(
            (course) => course._id === courseId
          );
          
          if (updatedCourseIndex !== -1) {
            const updatedCourse = {
              ...coursesArray[updatedCourseIndex],
              ...updatedData,
            };
            
            if (isDirectArray) {
              state.AllCourseslist = [
                ...coursesArray.slice(0, updatedCourseIndex),
                updatedCourse,
                ...coursesArray.slice(updatedCourseIndex + 1),
              ];
            } else {
              state.AllCourseslist.courses = [
                ...coursesArray.slice(0, updatedCourseIndex),
                updatedCourse,
                ...coursesArray.slice(updatedCourseIndex + 1),
              ];
            }
          }
        }
        
        state.loading = false;
        state.error = null;
        toast.success(t("store.courses.updatedSuccessfully"));
      })
      // extraReducers handler: updateCourse.rejected
      .addCase(updateCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        toast.error(t("store.courses.updateFailed"));
      })
      // extraReducers handler: GetCourseByID.pending
      .addCase(GetCourseByID.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: GetCourseByID.fulfilled
      .addCase(GetCourseByID.fulfilled, (state, action) => {
        state.CourseByID = action.payload;
        state.loading = false;
        state.error = null;
      })
      // extraReducers handler: GetCourseByID.rejected
      .addCase(GetCourseByID.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // SearchCoursebyName
      .addCase(SearchCoursebyName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: SearchCoursebyName.fulfilled
      .addCase(SearchCoursebyName.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          state.courseslist = action.payload.data.founded_Course;
          state.loading = false;
          state.error = null;
        } else {
          state.courseslist = action.payload.data;
          state.loading = false;
          toast.warning(action.payload, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
          });
        }
      })
      // extraReducers handler: SearchCoursebyName.rejected
      .addCase(SearchCoursebyName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default coursesSlice.reducer;
